// FieldDesk — AuthContext для web-dispatcher.
//
// Два пути входа:
//   1) Embedded (основной): BX24.getAuth() → POST /api/auth/bitrix → JWT.
//      Всё автоматически при старте.
//   2) Standalone (отладочный): JWT сохранён в sessionStorage (после первого
//      embedded-входа). Если нет — ошибка "откройте через Битрикс24".
//
// JWT хранится в sessionStorage под ключом `fd.jwt`. AuthUser — в state.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiClient, ApiError } from '../api/client';
import type { AuthUser } from '../api/types';
import { isEmbedded, readAuthParams, readPlacement, waitForBX24, type PlacementContext } from '../bitrix/bx24';

const STORAGE_KEY = 'fd.jwt';
const USER_KEY = 'fd.user';

interface PlacementInfo extends PlacementContext {}

export type AuthState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser; token: string; placement: PlacementInfo | null }
  | { status: 'unauthenticated'; reason: 'no-bx24' | 'auth-failed' | 'network'; message: string };

interface AuthContextValue {
  state: AuthState;
  api: ApiClient;
  embedded: boolean;
  logout: () => void;
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const tokenRef = useRef<string | null>(sessionStorage.getItem(STORAGE_KEY));
  const [state, setState] = useState<AuthState>({ status: 'idle' });
  const embedded = useMemo(() => isEmbedded(), []);

  const api = useMemo(
    () =>
      new ApiClient({
        getToken: () => tokenRef.current,
        onUnauthorized: () => {
          tokenRef.current = null;
          sessionStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(USER_KEY);
          setState({
            status: 'unauthenticated',
            reason: 'auth-failed',
            message: 'Сессия истекла. Переоткройте приложение в Битрикс24.',
          });
        },
      }),
    [],
  );

  const bootstrap = useCallback(async () => {
    setState({ status: 'loading' });

    // Embedded: токен всегда запрашиваем заново у Б24 — он живой 1 час.
    if (embedded) {
      try {
        const bx = await waitForBX24();
        const params = readAuthParams(bx);
        if (!params) {
          setState({
            status: 'unauthenticated',
            reason: 'auth-failed',
            message: 'Битрикс24 не передал параметры авторизации. Переоткройте вкладку.',
          });
          return;
        }
        const placement = readPlacement(bx);
        const resp = await api.loginViaBitrix(params);
        tokenRef.current = resp.token;
        sessionStorage.setItem(STORAGE_KEY, resp.token);
        sessionStorage.setItem(USER_KEY, JSON.stringify(resp.user));
        // Домен портала — для ссылок «Открыть сделку» в standalone-фолбэке.
        if (resp.portalDomain) sessionStorage.setItem('fd.portal', resp.portalDomain);
        setState({ status: 'authenticated', user: resp.user, token: resp.token, placement });
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : (e as Error).message;
        setState({ status: 'unauthenticated', reason: 'auth-failed', message: msg });
      }
      return;
    }

    // Standalone: пробуем старый токен. Если нет — блокировка.
    const savedToken = sessionStorage.getItem(STORAGE_KEY);
    const savedUserRaw = sessionStorage.getItem(USER_KEY);
    if (savedToken && savedUserRaw) {
      try {
        const user = JSON.parse(savedUserRaw) as AuthUser;
        tokenRef.current = savedToken;
        setState({ status: 'authenticated', user, token: savedToken, placement: null });
        return;
      } catch {
        /* fall through to no-bx24 */
      }
    }
    setState({
      status: 'unauthenticated',
      reason: 'no-bx24',
      message: 'Диспетчерская работает только изнутри Битрикс24. Откройте вкладку «FieldDesk — Выезды» в карточке сделки.',
    });
  }, [api, embedded]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const logout = useCallback(() => {
    tokenRef.current = null;
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(USER_KEY);
    setState({
      status: 'unauthenticated',
      reason: 'auth-failed',
      message: 'Вы вышли из системы.',
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, api, embedded, logout, retry: bootstrap }),
    [state, api, embedded, logout, bootstrap],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
